import {createSlice} from '@reduxjs/toolkit'
import incidentService from '../../api/incident'
import {toast} from 'react-toastify'
import FormHelper from '../../utils/forms'
import {doNothing} from '../../utils/placeholder'
import {routes} from '../../routes'
import NavigationHelper from '../../utils/navigation'

export const incidentManagementSlice = createSlice({
    name: 'incident_management',
    initialState: {
        rulesets: [],
        incidents: [],
        schedules:[],
        webhookEvents: [],
        webhook: '',
        isLoadMoreEnabled: true,
        isScheduleLoading: true,
        isFetching: false,
        isLoadingEvents: true,
        isProcessing: false,
        isDeleting: false,
        teamIncidents: [],
        isButtonLoaderActive: false,
        incidentActions: []
    },
    reducers: {
        setRulesets: (state,
            {payload}) => {
            state.rulesets = payload
        },
        setIncidents: (state, {payload}) => {
            if (!payload.isLoadMore || payload.isReturned) {
                state.incidents = payload.data
                return
            }
            const isSame = (item) => state.incidents.some(localData => localData.id === item.id)
            const pushData = payload.data?.some(item => isSame(item))
            !pushData && (state.incidents = state.incidents.concat(payload.data || []))
        },
        setTeamIncidents: (state, {payload}) => {
            state.teamIncidents = payload
        },
        setIsLoadMoreEnabled: (state, {payload}) => {
            state.isLoadMoreEnabled = payload
        },
        setIsFetching: (state, {payload}) => {
            state.isFetching = payload
        },
        setIsScheduleLoading: (state, {payload}) => {
            state.isScheduleLoading = payload
        },
        setIsLoadingEvents: (state, {payload}) => {
            state.isLoadingEvents = payload
        },
        setIsProcessing: (state, {payload}) => {
            state.isProcessing = payload
        },
        setIsDeleting: (state, {payload}) => {
            state.isDeleting = payload
        },
        setIsButtonLoaderActive: (state, {payload}) => {
            state.isButtonLoaderActive = payload
        },
        setIncidentActions: (state, {payload}) => {
            state.incidentActions = payload
        },
        setSchedules: (state, {payload}) => {
            state.schedules = payload
        },
        setwebhook: (state, {payload}) => {
            state.webhook = payload
        },
        setWebhookEvents: (state, {payload}) => {
            if (!payload.isLoadMore) {
                state.webhookEvents = payload.data
                return
            }
            const isSame = (item) => state.webhookEvents.some(localData => localData.received_at === item.received_at)
            const pushData = payload.data?.some(item => isSame(item))
            !pushData && (state.webhookEvents = state.webhookEvents.concat(payload.data))
        }
    }
})

export const {
    setRulesets,
    setIncidents,
    setIsLoadMoreEnabled,
    setIsFetching,
    setIsLoadingEvents,
    setIsProcessing,
    setIsDeleting,
    setIsButtonLoaderActive,
    setIncidentActions,
    setSchedules,
    setwebhook,
    setWebhookEvents,
    setIsScheduleLoading,
    setTeamIncidents
} = incidentManagementSlice.actions

export default incidentManagementSlice.reducer

const queryParameterNames = {
    last_created_at: 'last_created_at',
    last_received_at: 'last_received_at',
    resource_type: 'resource_type',
    resource_id: 'resource_id',
    limit: 'limit'
}

const incidentsLimit = 10
const webhookEventsLimit = 10

export const getIncidents = (id, lastCreatedAt = '', showLoader = true, isLoadMore = false, resourceType = '', resourceId = '', isReturnedData = false) => {
    return async dispatch => {
        showLoader && dispatch(setIsFetching(true))
        dispatch(setIsButtonLoaderActive(true))
        try {
            const query = `${queryParameterNames.last_created_at}=${lastCreatedAt}&${queryParameterNames.limit}=${incidentsLimit}&${queryParameterNames.resource_type}=${resourceType}&${queryParameterNames.resource_id}=${resourceId}`

            const {data} = await incidentService.getIncidents(id, query)
            dispatch(setIncidents({
                data: data?.data?.incidents,
                isLoadMore,
                isReturned: isReturnedData
            }))

            if (!data?.meta || data?.meta?.count < incidentsLimit) {
                dispatch(setIsFetching(false))
                dispatch(setIsLoadMoreEnabled(false))
                return data?.data?.incidents
            }
            dispatch(setIsLoadMoreEnabled(true))
            return data?.data?.incidents
        } catch {
            doNothing()
        } finally {
            dispatch(setIsFetching(false))
            dispatch(setIsButtonLoaderActive(false))
        }
    }
}

export const getTeamIncidentsWithQuery = (resourceId, resourceType, lastCreatedAt = '', showLoader = true) => {
    return async(dispatch, getState) => {
        showLoader && dispatch(setIsFetching(true))
        const teamId = getState().teams.selectedTeamId
        try {
            const query = `resource_type=${resourceType}&resource_id=${resourceId}&last_created_at=${lastCreatedAt || ''}`
            const {data} = await incidentService.getIncidents(teamId, query)
            await dispatch(setTeamIncidents(data?.data?.incidents))
        } catch  {
            doNothing()
        } finally {
            showLoader &&  dispatch(setIsFetching(false))
        }
    }
}

export const createIncident = (id, formData, setFieldError, closeModal, setCheckedIncidentsIds) => {
    return async dispatch => {
        dispatch(setIsProcessing(true))
        try {
            const {data} = await incidentService.createIncident(id, formData)
            toast.success(data.message)
            dispatch(getIncidents(id, '', false))
            setCheckedIncidentsIds([])
            closeModal()
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message, setFieldError)
        }
        dispatch(setIsProcessing(false))
    }
}

export const updateIncident = (id, formData, setCheckedIncidentsIds, teamId, setFieldError, closeModal) => {
    return async dispatch => {
        dispatch(setIsProcessing(true))
        try {
            const {data} = await incidentService.updateIncident(id, formData)
            toast.success(data.message)
            dispatch(getIncidents(teamId, '', false))
            setCheckedIncidentsIds([])
            dispatch(setIsProcessing(false))
            if (typeof closeModal === 'function') closeModal()
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message, setFieldError)
        }
        dispatch(setIsProcessing(false))
    }
}

export const getIncidentActions = id => {
    return async dispatch => {
        try {
            const {data} = await incidentService.getIncidentActions(id)
            dispatch(setIncidentActions(data?.data?.actions))
        } catch (e) {
            return
        }
    }
}

export const getRulesets = (showLoader = true) => {
    return async dispatch => {
        showLoader && dispatch(setIsFetching(true))
        try {
            const {data} = await incidentService.getRulesets()
            const teamRulesets = data?.data?.team?.length ? data?.data?.team?.map(item => {
                return {
                    ...item,
                    level: 'team'
                }
            }) : []
            const organisationRulesets = data?.data?.organisation?.length ? data?.data?.organisation?.map(item => {
                return {
                    ...item,
                    level: 'organisation'
                }
            }) : []
            dispatch(setRulesets(teamRulesets.concat(organisationRulesets)))
        } catch (e) {
            doNothing()
        }
        dispatch(setIsFetching(false))
    }
}

export const createRuleset = (formData, actions, setFieldError, navigate) => {
    return async dispatch => {
        dispatch(setIsProcessing(true))
        try {
            const {data} = await incidentService.createRuleset(formData)
            const isActionsUpdated = dispatch(updateRulesetActions(data?.data?.ruleset?.id, actions))
            isActionsUpdated ? toast.success(data.message) : toast.error('Actions can not be created')
            dispatch(getRulesets(false))
            navigate(NavigationHelper.getLocalizedPath(routes.rulesets.paths))
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message, setFieldError)
        }
        dispatch(setIsProcessing(false))
    }
}

export const getWebhook = (exist = true) => {
    return async dispatch => {
        try {
            dispatch(setIsLoadingEvents(true))
            const {data} = await incidentService.getWebhook()
            let webhook = data?.data?.webhooks?.[0]
            if (!webhook && !exist) {
                await incidentService.createWebhook()
                dispatch(getWebhook(false))
                dispatch(setwebhook(webhook))
                return
            }
            dispatch(setwebhook(webhook))
            if (!webhook) {
                dispatch(setIsLoadingEvents(false))
            }
        } catch (e) {
            doNothing()
        }
    }
}

export const getWebhookEvents = (lastReceivedAt, isLoadMore = false, showLoader = false) => {
    return async(dispatch, getState) => {
        showLoader && dispatch(setIsLoadingEvents(true))
        isLoadMore && dispatch(setIsButtonLoaderActive(true))
        try {
            const query = `${queryParameterNames.last_received_at}=${lastReceivedAt}&${queryParameterNames.limit}=${webhookEventsLimit}`
            const {data} = await incidentService.getWebhookEvents(getState().incident_management.webhook?.id, query)
            data?.meta ? dispatch(setWebhookEvents({
                data: data?.data?.webhookEvents,
                isLoadMore
            })) : dispatch(setWebhookEvents({
                data: getState().incident_management.webhookEvents
            }))
            if (!data?.meta || data?.meta?.count < webhookEventsLimit) {
                dispatch(setIsLoadingEvents(false))
                dispatch(setIsLoadMoreEnabled(false))

                isLoadMore && dispatch(setIsButtonLoaderActive(false))
                return
            }
            dispatch(setIsLoadMoreEnabled(true))
        } catch (e) {
            doNothing()
        }
        isLoadMore && dispatch(setIsButtonLoaderActive(false))
        showLoader && dispatch(setIsLoadingEvents(false))
    }
}

export const updateRuleset = (id, formData, actions, setFieldError) => {
    return async dispatch => {
        dispatch(setIsProcessing(true))
        try {
            const {data} = await incidentService.updateRuleset(id, formData)
            const isActionsUpdated = await dispatch(updateRulesetActions(id, actions))
            isActionsUpdated ? toast.success(data.message) : toast.error('Actions can not be updated')
            dispatch(getRulesets(false))
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message, setFieldError)
        }
        dispatch(setIsProcessing(false))
    }
}

export const deleteRuleset = id => {
    return async dispatch => {
        dispatch(setIsDeleting(true))
        try {
            const {data} = await incidentService.deleteRuleset(id)
            toast.success(data.message)
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message)
        } finally {
            dispatch(setIsDeleting(false))
        }
    }
}

export const updateRulesetActions = (id, data) => {
    return async() => {
        try {
            await incidentService.updateRulesetActions(id, data)
            return true
        } catch (e) {
            return false
        }
    }
}

export const createSchedule = (teamId, formData, setFieldError) => {
    return async() => {
        try {
            const {data} = await incidentService.createSchedule(teamId, formData)
            toast.success(data?.message)
            return true
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message, setFieldError)
        }
    }
}

export const updateSchedule = (teamId, formData, setFieldError) => {
    return async dispatch => {
        dispatch(setIsDeleting(true))
        try {
            const {data} = await incidentService.updateSchedule(teamId, formData)
            toast.success(data?.message)
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message, setFieldError)
        }
        dispatch(setIsDeleting(false))
    }
}

export const deleteSchedule = (teamId, teamMemberId, createdTime) => {
    return async dispatch => {
        dispatch(setIsDeleting(true))
        try {
            const query = `team_member_id=${teamMemberId}&created_at=${createdTime}`
            const {data} = await incidentService.deleteSchedule(teamId, query)
            toast.success(data?.message)
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message)
        }
        dispatch(setIsDeleting(false))
    }
}

export const getAllMembersSchedule = (teamMembers) => {
    return async dispatch => {
        dispatch(setIsScheduleLoading(true))
        try {
            const updatedSchedules = []
            await Promise.all(
                teamMembers.map(async(member) => {
                    const {data} = await incidentService.getMembersSchedule(member.customer_id)
                    updatedSchedules.push({member, schedules:data?.data?.schedules})
                })
            )
            dispatch(setSchedules(updatedSchedules))
        } catch (e) {
            doNothing()
        }
        dispatch(setIsScheduleLoading(false))
    }
}
